import React, { useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';
import { Step, WizardControlButton } from 'components/Wizard';
import useMediaPlayback from 'hooks/useMediaPlayback';
import {
  entireAudioInstanceIdSelector,
  templateSelector,
  uploadProgressSelector,
} from 'redux/modules/template-wizard/selectors';
import { VideoTypes } from 'types';
import {
  AUDIOGRAM_CUTOFF_DURATION_STRING,
  AUDIOGRAM_CUTOFF_MILLIS,
} from 'utils/constants';
import { createChainedFunction } from 'utils/functions';
import ClipAudioStep, { ClipAudioStepProps } from '../../../ClipAudioStep';
import { TranscriptionFormValue } from '../../../types';
import ChooseAnotherTemplateLink from '../../ChooseAnotherTemplateLink';
import { block } from '../../utils';
import { AudioFlowStep } from './types';

type PickedAudioStepProps = Pick<
  ClipAudioStepProps,
  | 'clipperRef'
  | 'src'
  | 'onClearError'
  | 'onError'
  | 'onPresetChange'
  | 'onReady'
  | 'onRegionUpdate'
>;

export interface UseClipAudioStepConfig extends PickedAudioStepProps {
  hidden?: boolean;
  onNextClick?: () => void;
  onTranscriptionChange: (value: TranscriptionFormValue) => void;
  regionSelected?: boolean;
  transcription: TranscriptionFormValue;
}

interface UseClipAudioStepResult {
  step: Step<AudioFlowStep.CLIP_AUDIO>;
}

const defaultCompatibilityTypes = [];

export default function useClipAudioStep({
  clipperRef,
  hidden,
  onPresetChange,
  onClearError,
  onError,
  onNextClick,
  onReady,
  onRegionUpdate,
  onTranscriptionChange,
  regionSelected,
  src,
  transcription,
}: UseClipAudioStepConfig): UseClipAudioStepResult {
  const { pause, play, playing } = useMediaPlayback();
  const uploadProgress = useSelector(uploadProgressSelector);
  const entireAudioInstanceId = useSelector(entireAudioInstanceIdSelector);
  const template = useSelector(templateSelector);

  useEffect(() => {
    if (hidden) {
      pause();
    }
  }, [hidden, pause]);

  const compatibilityTypes =
    template?.compatibleVideoTypes ?? defaultCompatibilityTypes;

  const isShortClipOnly =
    compatibilityTypes.includes(VideoTypes.SHORT_CLIP) &&
    !compatibilityTypes.includes(VideoTypes.FULL_EPISODE);

  const transcriptionConfig = useMemo(
    () => ({
      transcriptionToggleAllowed: compatibilityTypes.includes(
        VideoTypes.SHORT_CLIP,
      ),
    }),
    [compatibilityTypes],
  );

  return {
    step: {
      completedName: 'Audio Clipped',
      component: (
        <ClipAudioStep
          {...{
            clipperRef,
            src,
            onClearError,
            onError,
            playing,
            transcription,
            uploadProgress,
          }}
          entireAudioInstanceId={src ? entireAudioInstanceId : undefined}
          className={block('clip-audio-step', { hidden })}
          maxDurationMillis={
            isShortClipOnly ? AUDIOGRAM_CUTOFF_MILLIS : Infinity
          }
          onPause={pause}
          onPlay={play}
          onStop={pause}
          onReady={onReady}
          onRegionUpdate={onRegionUpdate}
          onPresetChange={onPresetChange}
          onTranscriptionChange={onTranscriptionChange}
          transcriptionConfig={transcriptionConfig}
        />
      ),
      description: isShortClipOnly ? (
        <div>
          The template you selected supports up to{' '}
          {AUDIOGRAM_CUTOFF_DURATION_STRING} of audio, for longer clips{' '}
          <ChooseAnotherTemplateLink stepName="ClipAudioStep" />.
        </div>
      ) : (
        `Clips over ${AUDIOGRAM_CUTOFF_DURATION_STRING} cannot include captions or be opened in our advanced editor.`
      ),
      keepMounted: true,
      name: 'Clip Audio',
      renderNextButton: ({ onClick, ...props }) => (
        <WizardControlButton
          {...props}
          disabled={!regionSelected}
          onClick={createChainedFunction(onClick, onNextClick)}
          theme="next"
        >
          next
        </WizardControlButton>
      ),
      stepId: AudioFlowStep.CLIP_AUDIO,
      title: 'Select your audio clip',
    },
  };
}
